import { FC, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import { NetworkAccountService } from "../../../shared/services/contentNetwork/networkAccounts/networkAccountService";

import showAlert from "../../../shared/utils/generic";
import { NetworkResource } from "@models/networkResource";
import NetworkResourceEditModal from "@components/networkAccounts/NetworkResourceEditModal";
import NetworkResourceCreateModal from "@components/networkAccounts/NetworkResourceCreateModal";
import ModalDelete from "src/components/common/ModalDelete";
import socialMediaData from "src/shared/data/socialMediaData";
import "./networkResources.scss";
import { useArrivalHighlight } from "@/shared/hooks/useArrivalHighlight";


interface Props {
  organizationId: number;
  /** Notified after resources change, so hosts can refresh derived state. */
  onResourcesChanged?: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: string }> = {
  account: { label: "Account", icon: "bi-person-circle" },
  page: { label: "Page", icon: "bi-file-earmark" },
  profile: { label: "Profile", icon: "bi-person-badge" },
  board: { label: "Board", icon: "bi-grid-3x3" },
  youtube_channel: { label: "YouTube Channel", icon: "bi-youtube" },
};


const OrganizationNetworkResourcesTab: FC<Props> = ({ organizationId, onResourcesChanged }) => {
  const [resources, setResources] = useState<NetworkResource[]>([]);
  const highlight = useArrivalHighlight(".nr-row--highlight");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState<NetworkResource | null>(null);
  const [resourceToDelete, setResourceToDelete] = useState<NetworkResource | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    Record<number, "testing" | "connected" | "failed">
  >({});
  // Kept per resource so the reason survives dismissing the dialog.
  const [connectionErrors, setConnectionErrors] = useState<Record<number, string>>({});
  const [connectionFailure, setConnectionFailure] = useState<{
    resource: NetworkResource;
    message: string;
  } | null>(null);
  const accountService = new NetworkAccountService();

  useEffect(() => {
    loadResources();
  }, [organizationId]);

  // No setLoading(true) here: link/unlink actions in the edit modal trigger a
  // refetch while the modal is open, and the full-page Loader would unmount it.
  const loadResources = async () => {
    try {
      const allResources = await accountService.listNetworkResources({
        organization_ids: [organizationId],
      });
      setResources(allResources);
      onResourcesChanged?.();
    } catch (err) {
      console.error("Failed to load resources:", err);
      showAlert("Failed to load network resources", "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = () => {
    setShowCreateModal(true);
  };

  const handleEditResource = (resource: NetworkResource) => {
    setSelectedResource(resource);
    setShowEditModal(true);
  };

  const handleAuthorizeResource = (resource: NetworkResource) => {
    const accountService = new NetworkAccountService();
    accountService.startOAuthFlow(resource.id);
  };

  /*
      A failed check is the one place the network tells us exactly what is
      wrong, so the message is kept and shown rather than collapsed into a
      red dot. Both failure shapes carry one: `connected: false` with an
      `error`, or a thrown request error.
  */
  const handleTestConnection = async (resource: NetworkResource) => {
    setConnectionStatus((prev) => ({ ...prev, [resource.id]: "testing" }));
    try {
      const { connected, error } = await accountService.checkConnection(resource.id);
      setConnectionStatus((prev) => ({
        ...prev,
        [resource.id]: connected ? "connected" : "failed",
      }));
      if (connected) {
        setConnectionErrors((prev) => {
          const next = { ...prev };
          delete next[resource.id];
          return next;
        });
        return;
      }
      const message = error?.trim() || "The network refused the connection but gave no reason.";
      setConnectionErrors((prev) => ({ ...prev, [resource.id]: message }));
      setConnectionFailure({ resource, message });
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "The connection check could not be completed.";
      setConnectionStatus((prev) => ({ ...prev, [resource.id]: "failed" }));
      setConnectionErrors((prev) => ({ ...prev, [resource.id]: message }));
      setConnectionFailure({ resource, message });
    }
  };

  const handleDeleteResource = (resource: NetworkResource) => {
    setResourceToDelete(resource);
  };

  const confirmDeleteResource = async () => {
    if (!resourceToDelete) return;

    setDeleting(true);
    try {
      await accountService.deleteNetworkResource(resourceToDelete.id);
      showAlert("Resource deleted", "success", 2000);
      setResourceToDelete(null);
      loadResources();
    } catch (err) {
      console.error("Failed to delete resource:", err);
      showAlert("Failed to delete resource", "error", 3000);
    } finally {
      setDeleting(false);
    }
  };

  const handleResourceCreated = () => {
    loadResources();
  };

  // Group resources by network, then by type
  const groupResourcesByNetworkAndType = () => {
    const grouped: Record<string, Record<string, NetworkResource[]>> = {};

    resources.forEach((resource) => {
      const networkKey = resource.network.toLowerCase();
      const typeKey = resource.type.toLowerCase();

      grouped[networkKey] ??= {};
      grouped[networkKey][typeKey] ??= [];
      grouped[networkKey][typeKey].push(resource);
    });

    for (const networkKey of Object.keys(grouped)) {
      const types = grouped[networkKey];

      if (!types.account) continue;

      grouped[networkKey] = {
        account: types.account,
        ...Object.fromEntries(
          Object.entries(types).filter(([key]) => key !== "account")
        ),
      };
    }

    return grouped;
  };

  const renderResourceRow = (resource: NetworkResource) => {
    // Opened from a failed job: flash the account whose token expired. An org can have
    // a dozen, and "reconnect the account" is no help without saying which.
    const highlighted = highlight === `resource:${resource.id}`;
    const networkKey = resource.network.toLowerCase();
    const typeKey = resource.type.toLowerCase();
    const status = connectionStatus[resource.id];
    // These carry their own OAuth; the rest are configured elsewhere. Snapchat joined them
    // in MPN-1484 — the server grew the branch but this list was missed, so the flow existed
    // with no way to start it.
    const canAuthorize =
      (networkKey === "pinterest" && typeKey === "account") ||
      (networkKey === "google" && typeKey === "youtube_channel") ||
      (networkKey === "linkedin" && typeKey === "account") ||
      (networkKey === "vibe" && typeKey === "account") ||
      (networkKey === "snapchat" && typeKey === "account");

    // The row opens the editor; the actions inside it stop the click so they
    // do not also trigger it.
    const act = (fn: () => void) => (event: React.MouseEvent) => {
      event.stopPropagation();
      fn();
    };

    return (
      <div
        key={resource.id}
        className={`nr-row${highlighted ? " nr-row--highlight" : ""}`}
        role="button"
        tabIndex={0}
        title={`Edit ${resource.name}`}
        onClick={() => handleEditResource(resource)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleEditResource(resource);
          }
        }}
      >
        <span className="nr-row__main">
          <span className="nr-row__name">{resource.name}</span>
          {resource.external_id && <span className="nr-row__id">{resource.external_id}</span>}
        </span>

        {status === "connected" && (
          <span className="nr-pill nr-pill--ok">
            <span className="nr-pill__dot" />
            Connected
          </span>
        )}
        {status === "failed" && (
          <button
            type="button"
            className="nr-pill nr-pill--bad nr-pill--button"
            title="Show why the connection failed"
            onClick={act(() =>
              setConnectionFailure({
                resource,
                message: connectionErrors[resource.id] ?? "No reason was reported.",
              }),
            )}
          >
            <span className="nr-pill__dot" />
            No connection
          </button>
        )}

        {/* Reading, then the rarest action, then the common one, then destroy. */}
        <span className="nr-row__actions">
          {canAuthorize && (
            <button
              type="button"
              className="nr-row__action"
              onClick={act(() => handleAuthorizeResource(resource))}
              title="Authorize / reauthorize"
            >
              <i className="bi bi-shield-check" />
              Authorize
            </button>
          )}
          <button
            type="button"
            className="nr-row__action"
            onClick={act(() => handleTestConnection(resource))}
            disabled={status === "testing"}
            title="Test connection"
          >
            {status === "testing" ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-broadcast-pin" />
            )}
            {status === "testing" ? "Testing…" : "Test"}
          </button>
          <button
            type="button"
            className="nr-row__action nr-row__action--danger"
            onClick={act(() => handleDeleteResource(resource))}
            title="Delete"
          >
            <i className="bi bi-trash" />
          </button>
        </span>
        <span className="nr-row__chev" aria-hidden="true">
          ›
        </span>
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  const groupedResources = groupResourcesByNetworkAndType();

  // Rendered by both the empty state and the list, so they stay in one place.
  const modals = (
    <>
      <NetworkResourceCreateModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        organizationId={organizationId}
        onResourceCreated={handleResourceCreated}
      />

      <NetworkResourceEditModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        resource={selectedResource}
        onResourceUpdated={handleResourceCreated}
      />

      <ModalDelete
        visible={!!resourceToDelete}
        toggleVisibility={() => setResourceToDelete(null)}
        title="Network Resource"
        objectLabel={resourceToDelete?.name || ""}
        deleteFn={confirmDeleteResource}
        isLoading={deleting}
      />

      {/* The network's own words, unedited — that is the useful part. */}
      <Modal show={!!connectionFailure} onHide={() => setConnectionFailure(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="div" className="nr-fail__title">
            <span className="nr-pill nr-pill--bad">
              <span className="nr-pill__dot" />
              No connection
            </span>
            <span className="nr-fail__name">{connectionFailure?.resource.name}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="nr-fail__lede">
            {socialMediaData[connectionFailure?.resource.network.toLowerCase() ?? ""]?.label ??
              connectionFailure?.resource.network}{" "}
            rejected the check. What it reported:
          </div>
          <pre className="nr-fail__message">{connectionFailure?.message}</pre>
        </Modal.Body>
        <Modal.Footer>
          {connectionFailure && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary me-auto"
              onClick={() => {
                const resource = connectionFailure.resource;
                setConnectionFailure(null);
                void handleTestConnection(resource);
              }}
            >
              Test again
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setConnectionFailure(null)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );

  if (resources.length === 0) {
    return (
      <div className="nr-pane">
        <div className="nr-empty">
          <div className="nr-empty__title">No network resources yet</div>
          <div className="nr-empty__body">
            A network resource is the account, page or profile an ad actually runs from. Connect
            one to start running campaigns for this organization.
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={{ fontSize: "0.72rem" }}
            onClick={handleAddResource}
          >
            + Resource
          </button>
        </div>
        {modals}
      </div>
    );
  }

  return (
    <div className="nr-pane">
      <div className="nr-head">
        <span className="nr-head__title">Network Resources</span>
        <span className="nr-head__note">
          {resources.length} on this organization, grouped by network
        </span>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm ms-auto"
          style={{ fontSize: "0.72rem" }}
          onClick={handleAddResource}
        >
          + Resource
        </button>
      </div>

      {/*
          One section per network, always open. The accordion hid resources
          behind a click on a page whose whole job is showing you what is
          connected.
      */}
      {Object.entries(groupedResources).map(([networkKey, typeGroups]) => {
        const networkConfig = socialMediaData[networkKey] || {
          label: networkKey,
          icon: "bi-box",
          color: "#6c757d",
        };
        const totalCount = Object.values(typeGroups).reduce(
          (sum, group) => sum + group.length,
          0,
        );

        return (
          <div className="nr-section" key={networkKey}>
            <div className="nr-section__head">
              <span
                className="nr-section__logo"
                style={{
                  backgroundColor: networkConfig.color,
                  color: networkKey === "snapchat" ? "#000" : "#fff",
                }}
              >
                <i className={networkConfig.icon} />
              </span>
              <span className="nr-section__title">{networkConfig.label}</span>
              <span className="nr-section__count">{totalCount}</span>
            </div>

            {Object.entries(typeGroups).map(([typeKey, typeResources]) => {
              const typeConfig = TYPE_CONFIG[typeKey] || { label: typeKey, icon: "bi-circle" };
              return (
                <div key={typeKey}>
                  <div className="nr-type">
                    <i className={typeConfig.icon} />
                    {typeConfig.label}s
                  </div>
                  {typeResources.map(renderResourceRow)}
                </div>
              );
            })}
          </div>
        );
      })}

      {modals}
    </div>
  );
};

export default OrganizationNetworkResourcesTab;